import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, UserCog, KeyRound, CreditCard, LogOut } from 'lucide-react';
import api from '../lib/api';
import { getErrorMessage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import PageHeader from '../components/ui/PageHeader';
import GlassCard from '../components/ui/GlassCard';
import Avatar from '../components/ui/Avatar';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

export default function Profile() {
  const { user, updateUser, logout } = useAuth();
  const { toast } = useToast();
  const fileRef = useRef(null);

  const [name, setName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);

  const saveName = async () => {
    if (name.trim().length < 2) return toast.error('Name is too short.');
    setSavingName(true);
    try {
      const { data } = await api.patch('/user/profile', { name: name.trim() });
      updateUser(data.user);
      toast.success('Profile updated.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSavingName(false);
    }
  };

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error('Image must be under 4 MB.');
    const fd = new FormData();
    fd.append('avatar', file);
    setUploading(true);
    try {
      const { data } = await api.post('/user/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateUser({ avatarUrl: data.avatarUrl });
      toast.success('Avatar updated.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Upload failed.'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) return toast.error('New password must be 8+ characters.');
    if (pwd.next !== pwd.confirm) return toast.error('Passwords do not match.');
    setSavingPwd(true);
    try {
      await api.patch('/user/password', { currentPassword: pwd.current, newPassword: pwd.next });
      toast.success('Password changed.');
      setPwd({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Profile" subtitle="Manage your account and security." icon={UserCog} />

      {/* Identity */}
      <GlassCard className="p-6">
        <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
          <div className="relative">
            <Avatar src={user?.avatarUrl} name={user?.name} size={88} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 rounded-full border border-white/10 bg-surface-raised p-2 text-content shadow-soft transition-colors hover:bg-surface-overlay disabled:opacity-60"
              aria-label="Change avatar"
            >
              <Camera className="h-4 w-4" />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={onPickAvatar}
            />
          </div>
          <div className="text-center sm:text-left">
            <p className="text-lg font-semibold">{user?.name}</p>
            <p className="text-sm text-content-muted">{user?.email}</p>
            <span className="mt-2 inline-flex items-center rounded-full bg-brand-500/15 px-2.5 py-0.5 text-xs font-medium text-brand-300">
              {user?.plan || 'FREE'} plan
            </span>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end">
          <Input label="Display name" value={name} onChange={(e) => setName(e.target.value)} />
          <Button onClick={saveName} loading={savingName} className="shrink-0">
            Save
          </Button>
        </div>
      </GlassCard>

      {/* Password */}
      <GlassCard className="mt-5 p-6">
        <div className="mb-4 flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-content-muted" />
          <h2 className="font-semibold">Change password</h2>
        </div>
        <form onSubmit={changePassword} className="grid gap-4 sm:grid-cols-3">
          <Input
            type="password"
            label="Current"
            value={pwd.current}
            onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
            autoComplete="current-password"
          />
          <Input
            type="password"
            label="New"
            value={pwd.next}
            onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
            autoComplete="new-password"
          />
          <Input
            type="password"
            label="Confirm"
            value={pwd.confirm}
            onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
            autoComplete="new-password"
          />
          <div className="sm:col-span-3">
            <Button type="submit" variant="secondary" loading={savingPwd}>
              Update password
            </Button>
          </div>
        </form>
      </GlassCard>

      {/* Billing + session */}
      <GlassCard className="mt-5 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="h-5 w-5 text-content-muted" />
            <div>
              <p className="font-medium">Billing & plan</p>
              <p className="text-sm text-content-muted">Manage your subscription and usage limits.</p>
            </div>
          </div>
          <Link to="/app/pricing">
            <Button variant="secondary">Manage plan</Button>
          </Link>
        </div>
        <div className="mt-5 border-t border-border/60 pt-5">
          <Button variant="ghost" icon={LogOut} onClick={logout} className="text-danger">
            Log out
          </Button>
        </div>
      </GlassCard>
    </div>
  );
}
